import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AppShell } from './components/layout/AppShell';
import { TasksView } from './views/TasksView';
import { ClientsView } from './views/ClientsView';
import { UsersView } from './views/UsersView';
import { WebsitesView } from './views/WebsitesView';
import { FormsView } from './views/FormsView';
import { ProposalsView } from './views/ProposalsView';
import { LeadsView } from './views/LeadsView';
import { ProjectsView } from './views/ProjectsView';
import { ActivityView } from './views/ActivityView';
import { CommandCenterView } from './views/CommandCenterView';
import { SwitchboardView } from './views/SwitchboardView';
import { LeadCommandCenterView } from './views/LeadCommandCenterView';
import { LeadLoopView } from './views/lcc/LeadLoopView';
import { LeadsRecordsView } from './views/lcc/LeadsRecordsView';
import { LeadDetailView } from './views/lcc/LeadDetailView';
import { LccOrganizationsView } from './views/lcc/OrganizationsView';
import { LccContactsView } from './views/lcc/ContactsView';
import { FormSubmissionsView } from './views/lcc/FormSubmissionsView';
import { AiVerificationView } from './views/lcc/AiVerificationView';
import { LeadReportsView } from './views/lcc/LeadReportsView';
import { CommunicationsView } from './views/CommunicationsView';
import { NotFoundView } from './views/NotFoundView';
import { SettingsView } from './views/SettingsView';
import { AliceFrameworkView } from './views/AliceFrameworkView';
import { ManagementUiKitView } from './views/ManagementUiKitView';
import { DesignLabView } from './views/DesignLabView';
import { OAuthForwardView } from './views/OAuthForwardView';
import { ManagedTableView } from './views/ManagedTableView';
import { useKonami } from './lib/use-konami';
import { useUiStore } from './stores/ui';
import { demoHomeRoute } from './platform/demo-mode';

// Lazy-loaded so the Markdown editor + viewer bundle stays out of the main chunk.
const MdVaultView = lazy(() =>
  import('./views/MdVaultView').then((m) => ({ default: m.MdVaultView })),
);

// Lazy-loaded — Excalidraw + React Flow are heavy and only needed on /whiteboard.
const WhiteboardView = lazy(() =>
  import('./views/WhiteboardView').then((m) => ({ default: m.WhiteboardView })),
);

function RouteFallback() {
  return (
    <div className="flex h-64 items-center justify-center text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

function BackstageKonami() {
  const toggleBackstage = useUiStore((state) => state.toggleBackstage);
  const setNotice = useUiStore((state) => state.setNotice);
  useKonami(() => {
    const on = toggleBackstage();
    setNotice(on ? 'Backstage unlocked.' : 'Backstage closed.');
  });
  return null;
}

export function App() {
  return (
    <AppShell>
      <BackstageKonami />
      <Routes>
        <Route path="/" element={<Navigate to={demoHomeRoute()} replace />} />
        <Route path="/demo/lead-command-center" element={<LeadCommandCenterView />} />
        {/* Lead Command Center demo views (Supabase-native demo Worker). */}
        <Route path="/demo/lcc/loop" element={<LeadLoopView />} />
        {/* Old inbox/follow-ups tabs merged into LeadLoop; keep the URLs alive. */}
        <Route path="/demo/lcc/inbox" element={<Navigate to="/demo/lcc/loop" replace />} />
        <Route path="/demo/lcc/follow-ups" element={<Navigate to="/demo/lcc/loop" replace />} />
        <Route path="/demo/lcc/approvals" element={<Navigate to="/demo/lcc/loop" replace />} />
        <Route path="/demo/lcc/leads" element={<LeadsRecordsView />} />
        <Route path="/demo/lcc/leads/:leadId" element={<LeadDetailView />} />
        <Route path="/demo/lcc/organizations" element={<LccOrganizationsView />} />
        <Route path="/demo/lcc/organizations/:orgId" element={<LccOrganizationsView />} />
        <Route path="/demo/lcc/contacts" element={<LccContactsView />} />
        <Route path="/demo/lcc/contacts/:contactId" element={<LccContactsView />} />
        <Route path="/demo/lcc/submissions" element={<FormSubmissionsView />} />
        <Route path="/demo/lcc/verification" element={<AiVerificationView />} />
        <Route path="/demo/lcc/reports" element={<LeadReportsView />} />
        <Route path="/tasks" element={<TasksView />} />
        <Route path="/tasks/:taskId" element={<TasksView />} />
        <Route path="/clients" element={<ClientsView />} />
        <Route path="/clients/:clientId" element={<ClientsView />} />
        <Route path="/clients/forms" element={<FormsView />} />
        <Route path="/clients/forms/entries" element={<FormsView />} />
        <Route path="/clients/forms/entries/:entryId" element={<FormsView />} />
        <Route path="/clients/forms/:formId" element={<FormsView />} />
        <Route path="/websites" element={<WebsitesView />} />
        <Route path="/websites/:websiteId" element={<WebsitesView />} />
        <Route path="/pipeline/leads" element={<LeadsView />} />
        <Route path="/pipeline/leads/:leadId" element={<LeadsView />} />
        <Route path="/proposals" element={<ProposalsView />} />
        <Route path="/proposals/:proposalId" element={<ProposalsView />} />
        <Route path="/users" element={<UsersView />} />
        <Route path="/users/:userId" element={<UsersView />} />
        <Route path="/work/projects" element={<ProjectsView />} />
        <Route path="/work/projects/:taskId" element={<ProjectsView />} />
        <Route path="/work/time-entries" element={<ManagedTableView tableKey="timeEntries" />} />
        <Route path="/work/time-entries/:recordId" element={<ManagedTableView tableKey="timeEntries" />} />
        <Route path="/billing/invoices" element={<ManagedTableView tableKey="invoices" />} />
        <Route path="/billing/invoices/:recordId" element={<ManagedTableView tableKey="invoices" />} />
        <Route path="/billing/subscriptions" element={<ManagedTableView tableKey="subscriptions" />} />
        <Route path="/billing/subscriptions/:recordId" element={<ManagedTableView tableKey="subscriptions" />} />
        <Route path="/billing/payments" element={<ManagedTableView tableKey="payments" />} />
        <Route path="/billing/payments/:recordId" element={<ManagedTableView tableKey="payments" />} />
        <Route path="/ai-systems/agents" element={<ManagedTableView tableKey="agents" />} />
        <Route path="/ai-systems/agents/:recordId" element={<ManagedTableView tableKey="agents" />} />
        <Route path="/ai-systems/calls" element={<ManagedTableView tableKey="calls" />} />
        <Route path="/ai-systems/calls/:recordId" element={<ManagedTableView tableKey="calls" />} />
        <Route path="/ai-systems/prompts" element={<ManagedTableView tableKey="prompts" />} />
        <Route path="/ai-systems/prompts/:recordId" element={<ManagedTableView tableKey="prompts" />} />
        <Route path="/knowledge/quick-notes" element={<CommunicationsView />} />
        <Route
          path="/knowledge/md-vault"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MdVaultView />
            </Suspense>
          }
        />
        <Route path="/activity" element={<ActivityView />} />
        <Route path="/command-center" element={<CommandCenterView />} />
        <Route path="/command-center/switchboard" element={<SwitchboardView />} />
        <Route
          path="/whiteboard"
          element={
            <Suspense fallback={<RouteFallback />}>
              <WhiteboardView />
            </Suspense>
          }
        />
        <Route path="/dev/ui-kit" element={<ManagementUiKitView />} />
        <Route path="/design-lab" element={<DesignLabView />} />
        <Route path="/settings/alice" element={<AliceFrameworkView />} />
        <Route path="/settings/*" element={<SettingsView />} />
        <Route path="/api/oauth/google/start" element={<OAuthForwardView />} />
        <Route path="/api/oauth/google/callback" element={<OAuthForwardView />} />
        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </AppShell>
  );
}
